import NProgress from 'nprogress';
import { Routes, Route, Navigate, Outlet } from 'react-router';
import { Suspense, Fragment, lazy, useEffect, ElementType } from 'react';
import { makeStyles } from '@mui/styles';

import { PATH_PAGE } from './paths';
import LoadingScreen from '../components/LoadingScreen';
import AuthProtect from '../components/Auth/AuthProtect';
import AppRoutes from '@/routes/AppRoutes';
import { AppRoute } from './types';

const nprogressStyle = makeStyles((theme: any) => ({
  '@global': {
    '#nprogress': {
      pointerEvents: 'none',
      '& .bar': {
        top: 0,
        left: 0,
        height: 2,
        width: '100%',
        position: 'fixed',
        zIndex: theme.zIndex.snackbar,
        backgroundColor: theme.palette.primary.main,
        boxShadow: `0 0 2px ${theme.palette.primary.main}`
      },
      '& .peg': {
        right: 0,
        opacity: 1,
        width: 100,
        height: '100%',
        display: 'block',
        position: 'absolute',
        transform: 'rotate(3deg) translate(0px, -4px)',
        boxShadow: `0 0 10px ${theme.palette.primary.main}, 0 0 5px ${theme.palette.primary.main}`
      }
    }
  }
}));

function RouteProgress({ children }: { children: React.ReactNode }) {
  nprogressStyle();

  NProgress.configure({
    speed: 500,
    showSpinner: false
  });

  useEffect(() => {
    NProgress.done();
    return () => {
      NProgress.start();
    };
  }, []);

  return children;
}

function renderRouteElements(routes: AppRoute[] = []) {
  return routes.map((route, i) => {
    const Layout = route.layout || Fragment;
    const Guard = route.guard || Fragment;
    let Element: ElementType = Outlet;

    if ('element' in route) {
      Element = route.element!;
    }

    const hasNested = 'routes' in route && route.routes.length > 0;

    const Wrapper = () => (
      <Guard>
        <Layout>
          <RouteProgress>
            <Element />
          </RouteProgress>
        </Layout>
      </Guard>
    );

    return (
      <Route key={i} path={route.path} element={<Wrapper />}>
        {hasNested && renderRouteElements(route.routes)}
      </Route>
    );
  });
}

export function renderRoutes(routes: AppRoute[] = []) {
  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>{renderRouteElements(routes)}</Routes>
    </Suspense>
  );
}

export function MainRouter() {
  return renderRoutes(routes);
}

const LoginView = lazy(() => import('../views/Auth/LoginView'));
const NewPasswordView = lazy(() => import('../views/Auth/NewPasswordView'));
const NewPinView = lazy(() => import('../views/Auth/NewPinView'));
const ResetPasswordView = lazy(() => import('../views/Auth/ResetPasswordView'));
const Page404View = lazy(() => import('../views/Errors/Page404View'));
const Page500View = lazy(() => import('../views/Errors/Page500View'));
const Maintenance = lazy(() => import('../views/Errors/Maintenance'));

const NavigateToLogin = () => <Navigate to={PATH_PAGE.auth.login} />;

const routes: AppRoute[] = [
  // Others Routes
  {
    guard: AuthProtect,
    path: PATH_PAGE.auth.login,
    element: LoginView
  },
  {
    path: PATH_PAGE.auth.newPassword,
    element: NewPasswordView
  },
  {
    path: PATH_PAGE.auth.newPin,
    element: NewPinView
  },
  {
    path: PATH_PAGE.auth.resetPassword,
    element: ResetPasswordView
  },
  {
    path: '/404',
    element: Page404View
  },
  {
    path: '/500',
    element: Page500View
  },
  {
    path: '/maintenance',
    element: Maintenance
  },
  {
    path: PATH_PAGE.auth.root,
    element: NavigateToLogin
  },

  // App Routes
  AppRoutes
];
